import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import ContentBox from '../components/ContentBox';
import HabboImage from '../components/HabboImage';
import { getAvatarBgStyle } from '../services/profileColorService';
import {
    getAppreciationConfig,
    getAppreciationWall,
    checkAppreciationHabbo,
    createAppreciationNomination,
    likeAppreciationPost,
    unlikeAppreciationPost,
} from '../services/api';

const MESSAGE_MAX_CHARS = 300;
const DEFAULT_BG = '#5A2929';

const SORT_OPTIONS = [
    { value: 'latest', label: 'Latest first' },
    { value: 'likes', label: 'Most loved' },
];

const timeAgo = (ts) => {
    if (!ts) return '';
    const seconds = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

function PaginationButton({ onClick, disabled, active, children }) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={[
                'font-["Jersey_10"] uppercase text-[15px] tracking-[1px] px-3 py-1 transition-all',
                active
                    ? 'bg-maroon text-white shadow-[0_2px_0_0_rgba(0,0,0,0.3)]'
                    : disabled
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-offwhite border border-gray-300 text-gray-700 hover:bg-gray-100 shadow-[0_2px_0_0_rgba(0,0,0,0.2)]',
            ].join(' ')}
        >
            {children}
        </button>
    );
}

function SkeletonCard() {
    return (
        <div className="border-2 border-black/40 bg-[#f7eef0] p-3 h-[210px] animate-pulse flex flex-col gap-3">
            <div className="flex gap-3">
                <div className="w-[70px] h-[70px] bg-gray-300 rounded" />
                <div className="flex-1 flex flex-col gap-2 pt-1">
                    <div className="h-5 bg-gray-300 rounded w-2/3" />
                    <div className="h-3 bg-gray-200 rounded w-1/3" />
                </div>
            </div>
            <div className="h-3 bg-gray-200 rounded" />
            <div className="h-3 bg-gray-200 rounded w-5/6" />
            <div className="mt-auto h-7 bg-gray-200 rounded" />
        </div>
    );
}

function WallCard({ post, canLike, onToggleLike }) {
    const [busy, setBusy] = useState(false);

    const handleLike = async () => {
        if (busy) return;
        setBusy(true);
        await onToggleLike(post);
        setBusy(false);
    };

    const nameNode = post.toIsMember ? (
        <Link
            to={`/members/profile/${encodeURIComponent(post.toHabboName)}`}
            className="font-['Jersey_10'] uppercase text-[24px] leading-none tracking-[1px] text-maroon hover:underline break-all"
        >
            {post.toHabboName}
        </Link>
    ) : (
        <span className="font-['Jersey_10'] uppercase text-[24px] leading-none tracking-[1px] text-coal break-all">
            {post.toHabboName}
        </span>
    );

    return (
        <div className="group border-2 border-black/40 bg-[#fdf6f2] flex flex-col transition-transform hover:-translate-y-0.5 shadow-[0_3px_0_0_rgba(0,0,0,0.35)]">
            <div className="flex gap-3 p-3 pb-2">
                <div
                    className="w-[70px] h-[70px] shrink-0 relative overflow-hidden rounded border-2 border-black/25"
                    style={getAvatarBgStyle(post.toAvatarBg || DEFAULT_BG)}
                >
                    <HabboImage
                        habbo={post.toHabboName}
                        hotel={post.toHotel || 'COM'}
                        size="b"
                        direction={2}
                        head_direction={3}
                        gesture="sml"
                        headonly
                        className="absolute left-1/2 top-[34px] -translate-x-1/2 -translate-y-1/2 max-w-none"
                        alt={post.toHabboName}
                    />
                </div>

                <div className="flex-1 min-w-0 flex flex-col justify-center gap-1">
                    {nameNode}
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="bg-gold border border-black/40 px-1.5 py-[1px] text-[10px] uppercase tracking-wide">
                            Appreciated
                        </span>
                        <span className="text-[10px] uppercase tracking-wide text-coal/50">{timeAgo(post.createdAt)}</span>
                    </div>
                </div>
            </div>

            <div className="px-3 pb-2 flex-1">
                <p className="text-[13px] leading-snug text-coal whitespace-pre-wrap break-words">
                    {post.message}
                </p>
            </div>

            <div className="px-3 pb-3 mt-auto">
                <div className="border-t border-black/15 pt-2 flex items-center justify-between gap-2">
                    <span className="text-[11px] italic text-coal/60 truncate">
                        from {post.isAnonymous || !post.fromHabboName ? 'Anonymous' : post.fromHabboName}
                    </span>

                    <button
                        onClick={canLike ? handleLike : undefined}
                        disabled={!canLike || busy}
                        title={canLike ? (post.likedByViewer ? 'Remove your like' : 'Like this message') : 'Log in to like'}
                        className={[
                            'shrink-0 flex items-center gap-1.5 border border-black/25 rounded px-2 py-1 bg-[#ededed]/60 transition-colors font-volterBold text-[9px] text-coal',
                            !canLike ? 'cursor-default' : post.likedByViewer ? 'text-red-600 hover:bg-red-50' : 'hover:bg-white',
                        ].join(' ')}
                    >
                        <img
                            src={post.likedByViewer ? '/img/likeHeart-on.png' : '/img/likeHeart-off.png'}
                            style={{ imageRendering: 'pixelated', opacity: canLike ? 1 : 0.65 }}
                            alt=""
                        />
                        <span className="pt-[2px]">{post.likeCount}</span>
                    </button>
                </div>
            </div>
        </div>
    );
}

function NominateForm({ config, onNominated, onExpandChange }) {
    const [habboName, setHabboName] = useState('');
    const [target, setTarget] = useState(null);
    const [message, setMessage] = useState('');
    const [anonymous, setAnonymous] = useState(true);
    const [checking, setChecking] = useState(false);
    const [sending, setSending] = useState(false);

    const unlimited = Boolean(config?.unlimited);
    const cost = config?.nextCost ?? 0;
    const remaining = config?.remaining ?? 0;
    const outOfNominations = !unlimited && remaining <= 0;
    const cannotAfford = cost > 0 && !config?.canAfford;

    const resetTarget = () => {
        onExpandChange?.(false);
        setTarget(null);
        setMessage('');
    };

    const handleCheck = async (e) => {
        e.preventDefault();
        const name = habboName.trim();
        if (!name) {
            toast.warning('Enter a Habbo name first');
            return;
        }

        setChecking(true);
        try {
            const res = await checkAppreciationHabbo(name);
            if (res.success && res.habbo) {
                onExpandChange?.(true);
                setTarget(res.habbo);
                setHabboName(res.habbo.name);
            } else {
                setTarget(null);
                toast.error(res.message || 'We could not find that Habbo');
            }
        } catch {
            toast.error('Could not reach the server');
        } finally {
            setChecking(false);
        }
    };

    const handleSend = async () => {
        if (!target) return;
        const text = message.trim();
        if (text.length < 5) {
            toast.warning('Write a slightly longer message, at least 5 characters');
            return;
        }

        setSending(true);
        try {
            const res = await createAppreciationNomination({
                habboName: target.name,
                message: text,
                anonymous,
            });

            if (res.success) {
                toast.success(res.message || 'Your appreciation has been posted!');
                setHabboName('');
                setAnonymous(true);
                resetTarget();
                await onNominated();
            } else {
                toast.error(res.message || 'Could not post your nomination');
            }
        } catch {
            toast.error('Could not reach the server');
        } finally {
            setSending(false);
        }
    };

    if (!config) {
        return (
            <div className="border-2 border-dashed border-black/20 bg-[#fdf6f2] p-4 text-center">
                <div className="font-['Jersey_10'] uppercase text-[22px] tracking-[1px] text-coal/40">Loading...</div>
            </div>
        );
    }

    if (!config.enabled) {
        return (
            <div className="border-2 border-dashed border-black/30 bg-[#fdf6f2] p-4 text-center">
                <div className="font-['Jersey_10'] uppercase text-[22px] tracking-[1px] text-maroon">Nominations are paused</div>
                <p className="text-sm text-coal/70 mt-1">
                    New nominations are closed at the moment, but the wall below is still open to read.
                </p>
            </div>
        );
    }

    if (!config?.loggedIn) {
        return (
            <div className="border-2 border-dashed border-black/30 bg-[#fdf6f2] p-4 text-center">
                <div className="font-['Jersey_10'] uppercase text-[22px] tracking-[1px] text-maroon">Log in to nominate</div>
                <p className="text-sm text-coal/70 mt-1">
                    Members get {config?.monthlyLimit ?? 3} nominations a month, the first {config?.freeCount ?? 1} free.
                </p>
            </div>
        );
    }

    return (
        <div className="border-2 border-black/40 bg-[#fdf6f2] p-3 shadow-[0_3px_0_0_rgba(0,0,0,0.25)]">
            <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="font-['Jersey_10'] uppercase text-[22px] tracking-[1px] text-maroon leading-none">
                    Nominate someone
                </span>
                <span className="bg-gold border border-black/40 px-2 py-[1px] text-[11px] uppercase tracking-wide">
                    {unlimited ? 'Unlimited (admin)' : `${remaining} of ${config.monthlyLimit} left this month`}
                </span>
                <span
                    className={`border border-black/40 px-2 py-[1px] text-[11px] uppercase tracking-wide ${
                        cost === 0 ? 'bg-green-200' : 'bg-orange-200'
                    }`}
                >
                    {cost === 0 ? 'This one is free' : `This one costs ${cost} framp card${cost !== 1 ? 's' : ''}`}
                </span>
            </div>

            <p className="text-[12px] text-coal/70 mb-3 leading-snug">
                {unlimited ? (
                    <>Admin account: your nominations are always free and there is no monthly cap.</>
                ) : (
                    <>
                        Your first {config.freeCount} nomination{config.freeCount !== 1 ? 's' : ''} each month {config.freeCount !== 1 ? 'are' : 'is'} free.
                        After that each one costs {config.cardCost} framp card{config.cardCost !== 1 ? 's' : ''}, taken from your balance
                        (you currently have <strong>{config.completedCards}</strong>). Nominations reset on the 1st of each month.
                    </>
                )}
            </p>

            {outOfNominations ? (
                <div className="border border-black/25 bg-white/60 p-3 text-center text-sm text-coal/70">
                    You have used all your nominations this month. Come back on the 1st!
                </div>
            ) : !target ? (
                <form onSubmit={handleCheck} className="flex flex-col sm:flex-row gap-2">
                    <input
                        type="text"
                        value={habboName}
                        onChange={(e) => setHabboName(e.target.value)}
                        placeholder="Who deserves some love?"
                        maxLength={32}
                        disabled={checking}
                        className="flex-1 px-3 py-2 border-2 border-black/30 bg-white focus:outline-none focus:border-maroon text-sm"
                    />
                    <button
                        type="submit"
                        disabled={checking || !habboName.trim()}
                        className="font-['Jersey_10'] uppercase text-[16px] tracking-[1px] px-4 py-2 bg-maroon text-white hover:bg-red-900 transition-colors disabled:opacity-50"
                        style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.3)' }}
                    >
                        {checking ? 'Looking...' : 'Find them'}
                    </button>
                </form>
            ) : (
                <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3 border border-black/25 bg-white/70 p-2">
                        <div
                            className="w-[60px] h-[60px] shrink-0 relative overflow-hidden rounded border-2 border-black/25"
                            style={getAvatarBgStyle(target.avatarBg || DEFAULT_BG)}
                        >
                            <HabboImage
                                figure={target.figureString || null}
                                habbo={target.figureString ? null : target.name}
                                hotel="COM"
                                size="b"
                                direction={2}
                                head_direction={3}
                                gesture="sml"
                                headonly
                                className="absolute left-1/2 top-[30px] -translate-x-1/2 -translate-y-1/2 max-w-none"
                                alt={target.name}
                            />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-['Jersey_10'] uppercase text-[22px] leading-none tracking-[1px] text-maroon break-all">
                                {target.name}
                            </div>
                            <div className="text-[11px] text-coal/60 mt-1">
                                {target.isMember ? 'Frankly member' : 'Not a Frankly member (yet!)'}
                            </div>
                        </div>
                        <button
                            onClick={resetTarget}
                            className="shrink-0 font-['Jersey_10'] uppercase text-[13px] tracking-[1px] px-2 py-1 bg-gray-200 hover:bg-gray-300 border border-black/30"
                        >
                            Change
                        </button>
                    </div>

                    <div>
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value.slice(0, MESSAGE_MAX_CHARS))}
                            rows={4}
                            placeholder={`Tell ${target.name} why they're appreciated...`}
                            disabled={sending}
                            className="w-full px-3 py-2 border-2 border-black/30 bg-white focus:outline-none focus:border-maroon text-sm resize-none"
                        />
                        <div className="flex justify-between text-[11px] text-coal/50 mt-1">
                            <span>Be kind! Staff review the wall.</span>
                            <span>{message.length}/{MESSAGE_MAX_CHARS}</span>
                        </div>
                    </div>

                    <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={anonymous}
                            onChange={(e) => setAnonymous(e.target.checked)}
                            className="w-4 h-4 accent-maroon"
                        />
                        <span className="text-[12px] text-coal">
                            Post anonymously {anonymous ? '(shown as "from Anonymous")' : '(your name will be shown)'}
                        </span>
                    </label>

                    {cannotAfford && (
                        <div className="border border-red-400 bg-red-50 text-red-800 text-[12px] px-3 py-2">
                            This nomination costs {cost} framp card{cost !== 1 ? 's' : ''} and you only have {config.completedCards}.
                        </div>
                    )}

                    <button
                        onClick={handleSend}
                        disabled={sending || cannotAfford || message.trim().length < 5}
                        className="font-['Jersey_10'] uppercase text-[18px] tracking-[1px] px-4 py-2 bg-maroon text-white hover:bg-red-900 transition-colors disabled:opacity-50"
                        style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.3)' }}
                    >
                        {sending
                            ? 'Sending love...'
                            : cost === 0
                                ? 'Post to the wall (free)'
                                : `Post to the wall (${cost} framp card${cost !== 1 ? 's' : ''})`}
                    </button>
                </div>
            )}
        </div>
    );
}

function IAppreciateYou({ userData }) {
    const [config, setConfig] = useState(null);
    const [posts, setPosts] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [page, setPage] = useState(1);
    const [sort, setSort] = useState('latest');
    const [loading, setLoading] = useState(true);
    const [lockedAboutHeight, setLockedAboutHeight] = useState(null);
    const aboutColRef = useRef(null);

    const handleFormExpandChange = useCallback((expanded) => {
        if (!expanded) {
            setLockedAboutHeight(null);
        } else if (aboutColRef.current) {
            setLockedAboutHeight(h => h ?? aboutColRef.current.offsetHeight);
        }
    }, []);

    useEffect(() => {
        if (lockedAboutHeight == null) return undefined;
        const clear = () => setLockedAboutHeight(null);
        window.addEventListener('resize', clear);
        return () => window.removeEventListener('resize', clear);
    }, [lockedAboutHeight]);

    const loadConfig = useCallback(async () => {
        try {
            const res = await getAppreciationConfig();
            if (res.success) setConfig(res);
        } catch {
        }
    }, []);

    const loadWall = useCallback(async (targetPage, targetSort) => {
        setLoading(true);
        try {
            const res = await getAppreciationWall(targetPage, targetSort);
            if (res.success) {
                setPosts(res.posts || []);
                setPagination(res.pagination || null);
            } else {
                toast.error(res.message || 'Could not load the wall');
            }
        } catch {
            toast.error('Could not reach the server');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadConfig();
    }, [loadConfig]);

    useEffect(() => {
        loadWall(page, sort);
    }, [page, sort, loadWall]);

    const handleNominated = useCallback(async () => {
        await loadConfig();
        if (page === 1 && sort === 'latest') {
            await loadWall(1, 'latest');
        } else {
            setSort('latest');
            setPage(1);
        }
    }, [loadConfig, loadWall, page, sort]);

    const handleToggleLike = useCallback(async (post) => {
        const liked = post.likedByViewer;

        setPosts(prev => prev.map(p => p.id === post.id
            ? { ...p, likedByViewer: !liked, likeCount: Math.max(0, p.likeCount + (liked ? -1 : 1)) }
            : p));

        try {
            const res = liked ? await unlikeAppreciationPost(post.id) : await likeAppreciationPost(post.id);
            if (res.success) {
                setPosts(prev => prev.map(p => p.id === post.id
                    ? { ...p, likedByViewer: res.likedByViewer, likeCount: res.likeCount }
                    : p));
            } else {
                throw new Error(res.message || 'failed');
            }
        } catch (error) {
            setPosts(prev => prev.map(p => p.id === post.id
                ? { ...p, likedByViewer: liked, likeCount: Math.max(0, p.likeCount + (liked ? 1 : -1)) }
                : p));
            toast.error(error?.message || 'Could not update your like');
        }
    }, []);

    const goToPage = useCallback((p) => {
        if (!pagination) return;
        if (p < 1 || p > pagination.totalPages || p === page) return;
        setPage(p);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [pagination, page]);

    const pageButtons = useMemo(() => {
        if (!pagination || pagination.totalPages <= 1) return null;

        const { totalPages } = pagination;
        const range = [];
        for (let i = Math.max(2, page - 2); i <= Math.min(totalPages - 1, page + 2); i++) range.push(i);

        const pages = [1, ...range, totalPages].filter((v, i, arr) => arr.indexOf(v) === i);

        const buttons = [];
        let prev = null;
        for (const p of pages) {
            if (prev && p - prev > 1) {
                buttons.push(
                    <span key={`gap-${p}`} className="font-['Jersey_10'] text-gray-400 px-1 self-end pb-1">...</span>
                );
            }
            buttons.push(
                <PaginationButton key={p} onClick={() => goToPage(p)} active={p === page}>{p}</PaginationButton>
            );
            prev = p;
        }
        return buttons;
    }, [pagination, page, goToPage]);

    const canLike = Boolean(userData);

    return (
        <div className="w-full min-h-screen p-4 lg2:p-6 overflow-hidden">
            <div className="max-w-6xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-12 lg:gap-x-4">
                    <div
                        ref={aboutColRef}
                        className="lg:col-span-4 flex"
                        style={lockedAboutHeight != null ? { height: lockedAboutHeight } : undefined}
                    >
                        <ContentBox
                            theme="outlines"
                            tint="#e00018"
                            header="About IAY!"
                            icon="/img/likeHeart-on.png"
                            className="w-full flex flex-col"
                            contentClassName="flex-1 flex flex-col"
                        >
                            <div className="flex gap-3 items-start">
                                <div className="shrink-0 w-[64px] flex justify-center">
                                    <HabboImage
                                        habbo="Nineteen"
                                        hotel="COM"
                                        size="b"
                                        action="wav"
                                        direction={2}
                                        head_direction={3}
                                        gesture="sml"
                                        className="max-w-none"
                                        alt="Nineteen"
                                    />
                                </div>

                                <div className="flex-1 min-w-0 text-[12px] leading-snug text-coal/85 space-y-2">
                                    <p>
                                        Hiya, I'm <strong>Nineteen</strong>. This all kicked off with a wall full of stickies,
                                        everyone scribbling each other's names up in rooms with a kind word underneath.
                                        Gorgeous to read. An absolute nightmare for me to keep on top of.
                                    </p>
                                    <p>
                                        So I brought it here. Same warm fuzzy feelings, none of the sticky wrangling, and this
                                        time it's staying put. There might even be a prize or two going for the loveliest messages.
                                    </p>
                                </div>
                            </div>

                            <p className="mt-auto pt-3 font-['Jersey_10'] uppercase text-[16px] tracking-[1px] text-maroon">
                                Go on, make someone's day.
                            </p>
                        </ContentBox>
                    </div>

                    <div className="lg:col-span-8 flex">
                        <ContentBox
                            theme="outlines"
                            tint="#d81b46"
                            header="I Appreciate You"
                            icon="/img/likeHeart-on.png"
                            className="w-full flex flex-col"
                            contentClassName="flex-1 flex flex-col"
                        >
                            <p className="text-sm text-coal/80 mb-3 leading-snug">
                                Someone made your day? Say so. Pick a Habbo, leave them a kind message, and it goes up on the wall
                                for everyone to see, anonymously, unless you'd rather sign your name.
                            </p>

                            <NominateForm
                                config={config}
                                onNominated={handleNominated}
                                onExpandChange={handleFormExpandChange}
                            />
                        </ContentBox>
                    </div>
                </div>

                <ContentBox theme="outlines" tint="#f090b0" header="The Wall" icon="/img/likeHeart-on.png">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                        <span className="font-['Jersey_10'] uppercase text-[16px] tracking-[1px] text-coal/60">
                            {pagination ? `${pagination.totalItems} kind message${pagination.totalItems !== 1 ? 's' : ''}` : 'Loading...'}
                        </span>

                        <label className="flex items-center gap-2">
                            <span className="font-['Jersey_10'] uppercase text-[14px] tracking-[1px] text-coal/60">Sort by</span>
                            <select
                                value={sort}
                                onChange={(e) => { setSort(e.target.value); setPage(1); }}
                                className="px-2 py-1 border-2 border-black/30 bg-white text-sm focus:outline-none focus:border-maroon"
                            >
                                {SORT_OPTIONS.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </label>
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
                        </div>
                    ) : posts.length === 0 ? (
                        <div className="text-center py-10">
                            <div className="font-['Jersey_10'] uppercase text-[24px] tracking-[1px] text-coal/50">
                                The wall is empty
                            </div>
                            <p className="text-sm text-coal/60 mt-1">Be the first to appreciate someone!</p>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {posts.map(post => (
                                    <WallCard
                                        key={post.id}
                                        post={post}
                                        canLike={canLike}
                                        onToggleLike={handleToggleLike}
                                    />
                                ))}
                            </div>

                            {pagination && pagination.totalPages > 1 && (
                                <div className="flex flex-wrap items-center justify-between gap-3 mt-6 pt-4 border-t border-gray-200">
                                    <span className="text-xs text-gray-500 font-['Jersey_10'] uppercase tracking-wide">
                                        Page {pagination.page} of {pagination.totalPages}
                                    </span>
                                    <div className="flex items-center gap-1 flex-wrap">
                                        <PaginationButton onClick={() => goToPage(page - 1)} disabled={!pagination.hasPrevPage}>
                                            &lsaquo; Prev
                                        </PaginationButton>
                                        {pageButtons}
                                        <PaginationButton onClick={() => goToPage(page + 1)} disabled={!pagination.hasNextPage}>
                                            Next &rsaquo;
                                        </PaginationButton>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </ContentBox>
            </div>
        </div>
    );
}

export default IAppreciateYou;
